import {useState, useEffect, useMemo} from 'react';
import {ListItemLink} from '../list-item-link/list-item-link';
import {match, useLocation, useRouteMatch} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {Collapse} from '@mui/material';
import {
  Apps,
  Badge,
  Person,
  Lock,
  KeyboardArrowDown,
  MusicNote,
  Groups2,
  SwapHoriz,
  AccessTimeFilled,
  FactCheck,
  ManageAccounts,
  HowToReg,
  AssignmentInd,
  ListAlt,
} from '@mui/icons-material';
import {CaregiverPathExtensions} from '../caregiver-routes/caregiver-routes';
import {useAuth} from '../../contexts/auth/context';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {ScreenPaths} from '../../enums';
import {useOffice} from '../../contexts/office/context';
import {useCanViewAttendanceTrackerAssignments} from '../../hooks/use-can-view-attendance-tracker-assignments';
import {useCanViewAttendanceTrackerRegistry} from '../../hooks/use-can-view-attendance-tracker-registry';

export interface TeamSidePanelComponentsProps {
  basePath?: string;
  showExpanded?: boolean;
}

export const TeamSidePanelComponents = ({
  basePath,
  showExpanded: sidePanelExpanded,
}: TeamSidePanelComponentsProps) => {
  const {t} = useTranslation();
  const {hasReadPermission} = useAuth();
  const officeContext = useOffice();
  const canViewAttendanceTrackerAssignments = useCanViewAttendanceTrackerAssignments();
  const canViewAttendanceTrackerRegistry = useCanViewAttendanceTrackerRegistry();
  const route: match<{caregiver_id: string}> | null = useRouteMatch(
    `${basePath}/:caregiver_id/(applications|user-cards|playlists|profile|care-services)`
  );
  const {pathname} = useLocation();
  const caregiverId = route?.params?.caregiver_id;

  // Hold state
  const [showExpanded, setShowExpanded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showShiftsExpanded, setShowShiftsExpanded] = useState(false);
  const [shiftsExpanded, setShiftsExpanded] = useState(false);
  const [showAttendanceTrackerExpanded, setShowAttendanceTrackerExpanded] = useState(false);
  const [attendanceTrackerExpanded, setAttendanceTrackerExpanded] = useState(false);

  const isProfessionalCaregiverPath = useMemo(
    () =>
      Boolean(
        [
          `/${CaregiverPathExtensions.Profile}`,
          `/${CaregiverPathExtensions.Applications}`,
          `/${CaregiverPathExtensions.UserCards}`,
          `/${CaregiverPathExtensions.Playlists}`,
          `/${CaregiverPathExtensions.ServiceShits}`,
        ].some((path) => pathname.includes(`${caregiverId}${path}`))
      ),
    [caregiverId, pathname]
  );

  const hasAnyTeamProfessionalPermission =
    hasReadPermission(RolePermissionType.Team_Professionals) ||
    hasReadPermission(RolePermissionType.Team_Professionals_Profile) ||
    hasReadPermission(RolePermissionType.Team_Professionals_Applications) ||
    hasReadPermission(RolePermissionType.Team_Professionals_Cards) ||
    hasReadPermission(RolePermissionType.Team_Professionals_Playlists) ||
    hasReadPermission(RolePermissionType.Team_Professionals_CareServices);

  const handleExpandClick = () => {
    if (!isProfessionalCaregiverPath) return;
    setExpanded(!expanded);
  };

  const handleShiftsExpandClick = () => {
    setShiftsExpanded(!shiftsExpanded);
  };

  const handleAttendanceTrackerExpandClick = () => {
    setAttendanceTrackerExpanded(!attendanceTrackerExpanded);
  };

  // Main list styling
  const listItemsSx = {
    width: '180px',
    height: '40px',
    boxShadow: 'none',
    mx: '11px',
    px: '4px',
    '& .MuiTypography-root': {
      textOverflow: 'ellipsis',
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      fontSize: '14px',
      fontWeight: 500,
    },
  };

  // Dropdown list styling
  const dropdownListItemsSx = {
    ...listItemsSx,
    color: '#747474',
    backgroundColor: '#F9FBFF',
    paddingLeft: sidePanelExpanded ? '22px' : '0px',
    paddingRight: '0px',
    '& .MuiTypography-root': {
      fontSize: '12px',
      fontWeight: 500,
      color: '#747474',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '15px',
      color: '#747474',
    },
    '& svg': {
      fontSize: '15px',
      color: '#747474',
    },
    '&:hover': {
      filter: 'brightness(95%)',
      backgroundColor: '#F9FBFF',
    },
  };

  useEffect(() => {
    setExpanded(isProfessionalCaregiverPath);
    setShowExpanded(isProfessionalCaregiverPath);

    const isShiftsPath = pathname.includes(ScreenPaths.ProfessionalCaregiver_Shifts);
    setShiftsExpanded(isShiftsPath);
    setShowShiftsExpanded(isShiftsPath);

    const isAttendanceTrackerPath = pathname.includes(
      ScreenPaths.ProfessionalCaregiver_AttendanceTracker
    );
    setAttendanceTrackerExpanded(isAttendanceTrackerPath);
    setShowAttendanceTrackerExpanded(isAttendanceTrackerPath);
  }, [route, pathname, isProfessionalCaregiverPath]);

  useEffect(() => {
    return () => {
      // clear user form
      officeContext.clearUserForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {hasAnyTeamProfessionalPermission && (
        <>
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={basePath!}
            primary={t(translations.teamSideMenu.professionals)}
            icon={<Groups2 />}
            endIcon={
              route?.isExact || isProfessionalCaregiverPath ? (
                <KeyboardArrowDown
                  onClick={(e) => {
                    handleExpandClick();
                    e.preventDefault();
                  }}
                  sx={{rotate: showExpanded ? '180deg' : '0deg'}}
                />
              ) : undefined
            }
            exact={!(isProfessionalCaregiverPath || route?.isExact)}
            expanded={sidePanelExpanded}
            teamSideMenuDropdown
            teamFirstChild
            sx={listItemsSx}
          />
          <Collapse
            in={expanded}
            collapsedSize={0}
            orientation={'vertical'}
            timeout={{enter: 500, exit: 500}}
            onExiting={() => setShowExpanded(false)}
            onEntered={() => setShowExpanded(true)}
          >
            {/* Professionals > Profile */}
            {hasReadPermission(RolePermissionType.Team_Professionals_Profile) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${basePath}/${caregiverId}/${CaregiverPathExtensions.Profile}`}
                primary={t(translations.teamSideMenu.profileDropdown.profile)}
                icon={<Person />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}

            {/* Professionals > Care Services */}
            {hasReadPermission(RolePermissionType.Team_Professionals_CareServices) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${basePath}/${caregiverId}/${CaregiverPathExtensions.ServiceShits}`}
                primary={t(translations.teamSideMenu.profileDropdown.careServices)}
                icon={<AccessTimeFilled />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}

            {/* Professionals > Applications */}
            {hasReadPermission(RolePermissionType.Team_Professionals_Applications) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${basePath}/${caregiverId}/${CaregiverPathExtensions.Applications}`}
                primary={t(translations.teamSideMenu.profileDropdown.applications)}
                icon={<Apps />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}

            {/* Professionals > Cards */}
            {hasReadPermission(RolePermissionType.Team_Professionals_Cards) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${basePath}/${caregiverId}/${CaregiverPathExtensions.UserCards}`}
                primary={t(translations.teamSideMenu.profileDropdown.cards)}
                icon={<Badge />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}

            {/* Professionals > Playlists */}
            {hasReadPermission(RolePermissionType.Team_Professionals_Playlists) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${basePath}/${caregiverId}/${CaregiverPathExtensions.Playlists}`}
                primary={t(translations.teamSideMenu.profileDropdown.playLists)}
                icon={<MusicNote />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
          </Collapse>
        </>
      )}

      {(hasReadPermission(RolePermissionType.Team_Shifts_Management) ||
        hasReadPermission(RolePermissionType.Team_Shifts_Attributions)) && (
        <>
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${basePath}${ScreenPaths.ProfessionalCaregiver_Shifts}`}
            primary={t(translations.teamSideMenu.shifts.title)}
            icon={<SwapHoriz />}
            endIcon={
              <KeyboardArrowDown
                onClick={(e) => {
                  handleShiftsExpandClick();
                  e.preventDefault();
                }}
                sx={{rotate: showShiftsExpanded ? '180deg' : '0deg'}}
              />
            }
            expanded={sidePanelExpanded}
            teamSideMenuDropdown
            teamMiddleChild
            sx={listItemsSx}
          />
          <Collapse
            in={shiftsExpanded}
            collapsedSize={0}
            orientation={'vertical'}
            timeout={{enter: 500, exit: 500}}
            onExiting={() => setShowShiftsExpanded(false)}
            onEntered={() => setShowShiftsExpanded(true)}
          >
            {hasReadPermission(RolePermissionType.Team_Shifts_Management) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${basePath}${ScreenPaths.ProfessionalCaregiver_Shifts}${ScreenPaths.ProfessionalCaregiver_ShiftsManagement}`}
                primary={t(translations.teamSideMenu.shifts.shiftsManagement)}
                icon={<FactCheck />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {hasReadPermission(RolePermissionType.Team_Shifts_Attributions) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${basePath}${ScreenPaths.ProfessionalCaregiver_Shifts}${ScreenPaths.ProfessionalCaregiver_ShiftsAllocations}`}
                primary={t(translations.teamSideMenu.shifts.shiftsAllocations)}
                icon={<ManageAccounts />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
          </Collapse>
        </>
      )}
      {(canViewAttendanceTrackerAssignments || canViewAttendanceTrackerRegistry) && (
        <>
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${basePath}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}`}
            primary={t(translations.teamSideMenu.attendanceTracker.title)}
            icon={<HowToReg />}
            endIcon={
              <KeyboardArrowDown
                onClick={(e) => {
                  handleAttendanceTrackerExpandClick();
                  e.preventDefault();
                }}
                sx={{rotate: showAttendanceTrackerExpanded ? '180deg' : '0deg'}}
              />
            }
            expanded={sidePanelExpanded}
            teamSideMenuDropdown
            teamMiddleChild
            sx={listItemsSx}
          />
          <Collapse
            in={attendanceTrackerExpanded}
            collapsedSize={0}
            orientation={'vertical'}
            timeout={{enter: 500, exit: 500}}
            onExiting={() => setShowAttendanceTrackerExpanded(false)}
            onEntered={() => setShowAttendanceTrackerExpanded(true)}
          >
            {canViewAttendanceTrackerAssignments && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${basePath}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}${ScreenPaths.ProfessionalCaregiver_AttendanceTrackerAssignments}`}
                primary={t(translations.teamSideMenu.attendanceTracker.assignments)}
                icon={<AssignmentInd />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {canViewAttendanceTrackerRegistry && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${basePath}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}${ScreenPaths.ProfessionalCaregiver_AttendanceTrackerRegistry}`}
                primary={t(translations.teamSideMenu.attendanceTracker.registry)}
                icon={<ListAlt />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
          </Collapse>
        </>
      )}
      {hasReadPermission(RolePermissionType.Team_Professionals_CareServices) && (
        <ListItemLink
          showTooltip={!sidePanelExpanded}
          to={`${basePath}${ScreenPaths.ProfessionalCaregiver_CareServices}`}
          primary={t(translations.teamSideMenu.careServices)}
          icon={<AccessTimeFilled />}
          teamMiddleChild
          sx={listItemsSx}
        />
      )}
      {hasReadPermission(RolePermissionType.Team_Permissions) && (
        <ListItemLink
          showTooltip={!sidePanelExpanded}
          to={`${basePath}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}`}
          primary={t(translations.teamSideMenu.permissions)}
          icon={<Lock />}
          teamMiddleChild
          sx={listItemsSx}
        />
      )}
    </>
  );
};
